import { ColumnDef } from '@tanstack/react-table';
import { ServiceAccount, toShortPermission } from '@tinct/core-api-models';
import {
  Badge,
  BadgeStatus,
  CopyButton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@tinct/web-components';
import {
  CollectionColumnHeader,
  CollectionRowActions,
} from '@tinct/web-collection';
import { format } from 'date-fns';
import { IconBan, IconCircleCheck } from '@tabler/icons-react';
import { Pencil, RefreshCw, Trash } from 'lucide-react';

type UseServiceAccountListColumnsOptions = {
  onEdit?: (serviceAccount: ServiceAccount) => void;
  onRotateSecret?: (serviceAccount: ServiceAccount) => void;
  onDisable?: (serviceAccount: ServiceAccount) => void;
  onEnable?: (serviceAccount: ServiceAccount) => void;
  onDelete?: (serviceAccount: ServiceAccount) => void;
};

export function useServiceAccountListColumns(
  options: UseServiceAccountListColumnsOptions,
): ColumnDef<ServiceAccount>[] {
  const { onEdit, onRotateSecret, onDisable, onEnable, onDelete } = options;

  return [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Name" />
      ),
      cell: ({ row }) => (
        <span className="text-sm font-semibold">{row.original.name ?? '-'}</span>
      ),
      meta: { className: 'min-w-[200px]' },
      enableSorting: true,
    },
    {
      accessorKey: 'clientId',
      header: () => <span>Client ID</span>,
      cell: ({ row }) => {
        const clientId = row.original.clientId;
        if (!clientId) return <span>-</span>;
        return (
          <div className="flex items-center gap-1">
            <code className="font-mono text-xs">{clientId}</code>
            <CopyButton
              value={clientId}
              toastMessage="Client ID copied"
              className="size-6"
            />
          </div>
        );
      },
      enableSorting: false,
    },
    {
      id: 'permissions',
      header: () => <span>Permissions</span>,
      cell: ({ row }) => {
        const permissions = row.original.permissions;
        if (!permissions?.length) return <span>-</span>;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary">
                {permissions.length} permission{permissions.length > 1 ? 's' : ''}
              </Badge>
            </TooltipTrigger>
            <TooltipContent
              className="max-h-64 overflow-y-auto border border-border bg-popover text-popover-foreground shadow-md"
              arrowClassName="bg-popover fill-popover"
            >
              <div className="flex flex-col gap-0.5">
                {permissions.map((permission) => (
                  <span key={permission} className="font-mono text-xs">
                    {toShortPermission(permission)}
                  </span>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.original.status;
        if (status === 'active') return <BadgeStatus variant="green">Active</BadgeStatus>;
        if (status === 'disabled') return <BadgeStatus variant="yellow">Disabled</BadgeStatus>;
        if (status === 'deleted') return <BadgeStatus variant="red">Deleted</BadgeStatus>;
        return <span>-</span>;
      },
      enableSorting: true,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Created" />
      ),
      cell: ({ row }) => (
        <div className="text-sm">
          {row.getValue('createdAt')
            ? format(row.getValue('createdAt'), 'dd/MM/yyyy')
            : '-'}
        </div>
      ),
      enableSorting: true,
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const isDisabled = row.original.status === 'disabled';

        return (
          <div className="flex items-center justify-end gap-1">
            <CollectionRowActions
              row={row}
              actions={[
                {
                  label: 'Edit',
                  icon: Pencil,
                  onClick: (serviceAccount: ServiceAccount) => {
                    onEdit?.(serviceAccount);
                  },
                },
                {
                  label: 'Rotate secret',
                  icon: RefreshCw,
                  onClick: (serviceAccount: ServiceAccount) => {
                    onRotateSecret?.(serviceAccount);
                  },
                },
                isDisabled
                  ? {
                      label: 'Enable',
                      icon: IconCircleCheck,
                      onClick: (serviceAccount: ServiceAccount) => {
                        onEnable?.(serviceAccount);
                      },
                    }
                  : {
                      label: 'Disable',
                      icon: IconBan,
                      onClick: (serviceAccount: ServiceAccount) => {
                        onDisable?.(serviceAccount);
                      },
                    },
                {
                  label: 'Delete',
                  icon: Trash,
                  group: 'danger',
                  className: 'text-red-500!',
                  onClick: (serviceAccount: ServiceAccount) => {
                    onDelete?.(serviceAccount);
                  },
                },
              ]}
            />
          </div>
        );
      },
    },
  ];
}
